import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useMyPendingInvites } from '@/hooks/useMyPendingInvites';
import { formatInviteExpiry } from '@/lib/inviteExpiry';
import { ROLE_LABELS, type Role } from '@/types/roles';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';

/**
 * An invitation waiting for the person, wherever they are in the product.
 *
 * The invite email carries a link, and for a long time that link was the only way in.
 * Somebody who read the email on a phone and then signed in on a laptop had nothing to
 * find. It sits in the dashboard shell rather than on one screen because an invitation
 * arrives whatever the person happens to be doing: working in one organisation and asked
 * to join a second, or holding nothing but their own record.
 */
export function PendingInvitationsBanner() {
  const invites = useMyPendingInvites();
  const list = invites.data ?? [];

  if (list.length === 0) return null;

  return (
    <div className="border-b border-brand/30 bg-brand-soft/40 px-4 py-3 sm:px-6 lg:px-8 xl:px-10">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-3">
        {list.map((invite) => (
          <div
            key={invite.id}
            className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 items-start gap-3">
              <WorklynxIcon name="mail-open" size={20} className="mt-0.5 shrink-0 text-brand" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  {invite.organizationName} has invited you to join
                </p>
                <p className="mt-0.5 text-xs text-muted">
                  As {ROLE_LABELS[invite.role as Role] ?? invite.role} ·{' '}
                  {formatInviteExpiry(invite.expiresAt).relative}
                </p>
              </div>
            </div>
            <Button asChild size="sm" className="shrink-0 sm:self-center">
              <Link to={`/accept-invite?invite=${encodeURIComponent(invite.token)}`}>
                View invitation
              </Link>
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
